import {Dispatch, FC, SetStateAction, useContext, useRef, useState} from "react";
import {makeStyles} from "tss-react/mui";
import {S3Client} from "@aws-sdk/client-s3";
import {Upload} from "@aws-sdk/lib-storage";
import {AuthContext} from "@buildwitt/shapeshift-identity";
import {VideoUploadType} from "@/types/video";

const MAX_FILE_SIZE = 10 * 1024 * 1024 * 1024; // 10 GB

const useStyles = makeStyles()((_theme) => ({
	fileInput: {
		display: "none"
	}
}));

type InitReponseType = {
	videoId: string,
	fileName: string,
	credentials: {
		bucket: string;
		object_key: string;
		access_key_id: string;
		secret_access_key: string;
		session_token: string;
		video_id: string;
		api_request_url: string;
	}
};

type ResponseErrorType = { success: false, status?: number, statusText?: string, details?: any, error: string }
type ResponseBaseType<T> = ResponseErrorType | { success: true, data: T };

function isResponseError<T>(response: ResponseBaseType<T>): response is ResponseErrorType {
	return !response?.success;
}

type ICustomProps = {
	moduleType?: string,
	uploadState: VideoUploadType,
	setUploadState: Dispatch<SetStateAction<VideoUploadType>>,
	handleSuccess: (videoId: string) => void,
	children: (props: { onClick: () => void, progress: number, error?: string | null }) => JSX.Element,
	lessonId: number,
}

type IProps = ICustomProps;

export const UploadBrightcoveVideo: FC<IProps> = ({uploadState, setUploadState, handleSuccess, children, lessonId, moduleType = "lesson"}) => {
	const {cx, classes} = useStyles();
	const {jwt} = useContext(AuthContext);

	const inputRef = useRef<HTMLInputElement>(null);
	const [error, setError] = useState<string | null>(null);
	const [progress, setProgress] = useState(0);

	const handleClick = (): void => {
		inputRef.current?.click();
	}

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const files = e.target.files;
		const selectedFile = files?.[0];

		if (selectedFile && selectedFile.size > MAX_FILE_SIZE) {
			setError("File size exceeds the maximum limit of 10 GB");
			setUploadState("error");
			return;
		}

		if (selectedFile) {
			(async () => {
				setError(null);
				setUploadState("progress");
				try {
					await uploadFile(selectedFile, handleSuccess);
				} catch (err) {
					setError("Failed to upload video");
					setUploadState("error");
				}
			})()

			// setFile(e.target.files[0]);
		}

		if (inputRef.current) {
			inputRef.current.value = "";
		}
	};

	const uploadFile = async (file: File, successCb: (videoId: string) => void): Promise<void | boolean> => {
		setProgress(0);

		const uploadInit = await fetch(`${process.env.REACT_APP_API_HOST}/api/${moduleType}/video/brightcove/init`, {
			method: "POST",
			body: JSON.stringify({fileName: file.name}),
			headers: {
				"Content-Type": "application/json",
				"Accept": "application/json",
				"Authorization": `Bearer ${jwt}`
			}
		});

		if (!uploadInit.ok) {
			setError(`Failed to reach backend ${uploadInit.status} ${uploadInit.statusText}`);
			setUploadState("error");
			return;
		}

		const initResp: ResponseBaseType<InitReponseType> = await uploadInit.json();

		if (isResponseError(initResp)) {
			setError(`Failed to initialize upload ${initResp.error}`);
			setUploadState("error");
			return;
		}

		const s3 = new S3Client({
			region: "us-east-1",
			credentials: {
				accessKeyId: initResp.data.credentials.access_key_id,
				secretAccessKey: initResp.data.credentials.secret_access_key,
				sessionToken: initResp.data.credentials.session_token
			}
		});

		const uploader = new Upload({
			client: s3,
			params: {
				Bucket: initResp.data.credentials.bucket,
				Key: initResp.data.credentials.object_key,
				Body: file
			},
			// 5 Gb
			partSize : 5 * 1024 * 1024 * 1024
		});

		uploader.on("httpUploadProgress", (evt) => {
			if (evt.loaded && evt.total) {
				setProgress((evt.loaded / evt.total) * 100);
			}
		});

		await uploader.done();

		const ingestVideo = await fetch(`${process.env.REACT_APP_API_HOST}/api/${moduleType}/video/brightcove/complete`, {
			method: "POST",
			body: JSON.stringify({
				videoId: initResp.data.videoId,
				videoUrl: initResp.data.credentials.api_request_url,
				lessonId
			}),
			headers: {
				"Content-Type": "application/json",
				"Authorization": `Bearer ${jwt}`
			}
		});

		if (!ingestVideo.ok) {
			setError(`Failed to complete upload ${ingestVideo.status} ${ingestVideo.statusText}`);
			setUploadState("error");
			return;
		}

		const ingestResp: ResponseBaseType<any> = await ingestVideo.json();

		if (isResponseError(ingestResp)) {
			setError(ingestResp.error || "Failed to ingest video");
			setUploadState("error");
			return;
		}

		successCb(initResp.data.videoId);
		setUploadState("done");
		return true;
	};


	return (
		<>
			<input ref={inputRef} type="file" onChange={handleFileChange} accept="video/*" className={classes.fileInput} />
			{children ? children({onClick: handleClick, progress, error}) : <>Children need to be implemented</>}
		</>
	);
}
